import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Badge } from '../../styles/v2.styled';
import { checkSession } from '../../../Common/Utils/SsoSession';

const DAY_MS = 24 * 60 * 60 * 1000;

const Shell = styled.div`
  display: grid;
  gap: 18px;
`;

const Dashboard = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 18px;
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.section`
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.neutral[0]};
  overflow: hidden;
`;

const PanelHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  background: ${({ theme }) => theme.colors.neutral[50]};

  h3 {
    margin: 0;
    color: ${({ theme }) => theme.colors.neutral[900]};
    font-size: 1.08rem;
  }
`;

const PanelBody = styled.div`
  padding: 18px;
`;

const SegmentBar = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Button = styled.button`
  min-height: 38px;
  border: 1px solid ${({ $primary, theme }) => ($primary ? theme.colors.primary[600] : theme.colors.neutral[200])};
  border-radius: 8px;
  background: ${({ $primary, theme }) => ($primary ? theme.colors.primary[600] : theme.colors.neutral[0])};
  color: ${({ $primary, theme }) => ($primary ? theme.colors.neutral[0] : theme.colors.neutral[700])};
  -webkit-text-fill-color: ${({ $primary, theme }) => ($primary ? theme.colors.neutral[0] : theme.colors.neutral[700])};
  padding: 0 13px;
  font-weight: 900;
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.45 : 1)};
`;

const Input = styled.input`
  min-height: 40px;
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  border-radius: 8px;
  padding: 0 12px;
  color: ${({ theme }) => theme.colors.neutral[900]};
`;

const Field = styled.label`
  display: grid;
  gap: 6px;
  color: ${({ theme }) => theme.colors.neutral[700]};
  font-size: 0.86rem;
  font-weight: 900;
`;

const TaskList = styled.div`
  display: grid;
  gap: 10px;
`;

const TaskRow = styled.button`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.primary[400] : theme.colors.neutral[200])};
  border-radius: 8px;
  background: ${({ $active, theme }) => ($active ? theme.colors.primary[50] : theme.colors.neutral[0])};
  padding: 14px;
  text-align: left;
  cursor: pointer;

  strong {
    color: ${({ theme }) => theme.colors.neutral[900]};
  }

  span {
    display: block;
    margin-top: 5px;
    color: ${({ theme }) => theme.colors.neutral[500]};
    font-size: 0.86rem;
  }
`;

const Timeline = styled.div`
  display: grid;
  gap: 10px;
  min-width: 720px;
`;

const TimelineWrap = styled.div`
  overflow-x: auto;
`;

const TimelineHeader = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ $days }) => $days}, minmax(28px, 1fr));
  gap: 2px;
`;

const DayCell = styled.div`
  min-height: 28px;
  border-radius: 4px;
  background: ${({ $today, theme }) => ($today ? theme.colors.primary[600] : theme.colors.neutral[100])};
  color: ${({ $today, theme }) => ($today ? theme.colors.neutral[0] : theme.colors.neutral[500])};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72rem;
  font-weight: 900;
`;

const TimelineLine = styled.div`
  position: relative;
  height: 34px;
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.neutral[50]};
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
`;

const Bar = styled.div`
  position: absolute;
  top: 6px;
  height: 20px;
  border-radius: 6px;
  background: linear-gradient(90deg, #0891b2, #0f766e);
  color: #fff;
  font-size: 0.74rem;
  font-weight: 900;
  padding: 2px 8px;
  overflow: hidden;
  white-space: nowrap;
`;

const EditorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const TodoLayout = styled.div`
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 18px;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
`;

const DayButton = styled.button`
  min-height: 42px;
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.primary[500] : theme.colors.neutral[200])};
  border-radius: 8px;
  background: ${({ $active, $muted, theme }) => {
    if ($active) return theme.colors.primary[600];
    if ($muted) return theme.colors.neutral[50];
    return theme.colors.neutral[0];
  }};
  color: ${({ $active, $muted, theme }) => {
    if ($active) return theme.colors.neutral[0];
    if ($muted) return theme.colors.neutral[400];
    return theme.colors.neutral[800];
  }};
  -webkit-text-fill-color: ${({ $active, $muted, theme }) => {
    if ($active) return theme.colors.neutral[0];
    if ($muted) return theme.colors.neutral[400];
    return theme.colors.neutral[800];
  }};
  font-weight: 900;
  cursor: pointer;
`;

const TodoItem = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 10px;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  border-radius: 8px;
  padding: 12px;

  span {
    color: ${({ $done, theme }) => ($done ? theme.colors.neutral[400] : theme.colors.neutral[800])};
    text-decoration: ${({ $done }) => ($done ? 'line-through' : 'none')};
  }
`;

const Empty = styled.div`
  border: 1px dashed ${({ theme }) => theme.colors.neutral[300]};
  border-radius: 8px;
  padding: 24px;
  color: ${({ theme }) => theme.colors.neutral[500]};
  text-align: center;
`;

const AddRow = styled.form`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
  margin-bottom: 12px;
`;

function toISO(date) {
  const next = new Date(date);
  next.setMinutes(next.getMinutes() - next.getTimezoneOffset());
  return next.toISOString().slice(0, 10);
}

function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function monthCells(currentDate) {
  const first = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const start = addDays(first, -first.getDay());
  return Array.from({ length: 42 }, (_, index) => addDays(start, index));
}

async function postJson(url, body = {}) {
  const response = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const text = await response.text();
  if (!response.ok || !text) return null;
  return JSON.parse(text);
}

const SAMPLE_TASKS = [
  { id: 1, title: '커리큘럼 개선', worker: '교육팀', startDate: toISO(new Date()), endDate: toISO(addDays(new Date(), 4)) },
  { id: 2, title: '과제 피드백 정리', worker: '멘토', startDate: toISO(addDays(new Date(), 3)), endDate: toISO(addDays(new Date(), 9)) },
  { id: 3, title: '배포 점검', worker: '운영', startDate: toISO(addDays(new Date(), 8)), endDate: toISO(addDays(new Date(), 11)) },
];

function WorkspaceCalendarManager() {
  const [session, setSession] = useState({});
  const [view, setView] = useState('timeline');
  const [tasks, setTasks] = useState(SAMPLE_TASKS);
  const [selectedTaskId, setSelectedTaskId] = useState(1);
  const [taskForm, setTaskForm] = useState(SAMPLE_TASKS[0]);
  const [selectedDate, setSelectedDate] = useState(toISO(new Date()));
  const [visibleMonth, setVisibleMonth] = useState(new Date());
  const [todos, setTodos] = useState([]);
  const [todoText, setTodoText] = useState('');
  const [message, setMessage] = useState('');

  const isLoggedIn = session?.State === 'LOGIN';
  const days = useMemo(() => Array.from({ length: 30 }, (_, index) => addDays(new Date(), index - 5)), []);
  const cells = useMemo(() => monthCells(visibleMonth), [visibleMonth]);

  const selectedTask = useMemo(
    () => tasks.find((task) => String(task.id) === String(selectedTaskId)) || tasks[0],
    [selectedTaskId, tasks],
  );

  const loadTasks = useCallback(async () => {
    try {
      const data = await postJson('/request/SelectTaskList', {});
      if (Array.isArray(data) && data.length) {
        const normalized = data.map((task) => ({
          id: task.id,
          title: task.title || '-',
          worker: task.worker || '',
          startDate: task.startDate || task.startdate || '',
          endDate: task.endDate || task.enddate || '',
        }));
        setTasks(normalized);
        setSelectedTaskId(normalized[0]?.id);
        setTaskForm(normalized[0]);
      }
    } catch (error) {
      setMessage('태스크 데이터를 불러오지 못해 예시 데이터를 표시합니다.');
    }
  }, []);

  const loadTodos = useCallback(async (date) => {
    try {
      const data = await postJson('/request/GetToDoList', { ToDoDate: date });
      setTodos(Array.isArray(data) ? data : []);
    } catch (error) {
      setTodos([]);
    }
  }, []);

  useEffect(() => {
    checkSession().then((data) => setSession(data || {}));
    loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    loadTodos(selectedDate);
  }, [loadTodos, selectedDate]);

  useEffect(() => {
    if (selectedTask) setTaskForm(selectedTask);
  }, [selectedTask]);

  const saveTask = async (event) => {
    event.preventDefault();
    const nextTask = {
      ...taskForm,
      title: taskForm.title || '-',
      worker: taskForm.worker || session?.UserId || '',
    };
    setTasks((prev) => prev.map((task) => (String(task.id) === String(nextTask.id) ? nextTask : task)));
    try {
      await postJson('/request/taskRow', nextTask);
      setMessage('태스크가 저장되었습니다.');
    } catch (error) {
      setMessage('태스크 저장은 서버 로그인 후 반영됩니다.');
    }
  };

  const addTodo = async (event) => {
    event.preventDefault();
    if (!todoText.trim()) return;
    try {
      await postJson('/request/InsertToDoList', { ToDoDate: selectedDate, Contents: todoText.trim() });
      setTodoText('');
      await loadTodos(selectedDate);
    } catch (error) {
      setTodos((prev) => [
        { job_num: `local-${Date.now()}`, contents: todoText.trim(), complete_check: 'N' },
        ...prev,
      ]);
      setTodoText('');
    }
  };

  const updateTodo = async (todo, checked) => {
    const num = todo.job_num || todo.JOB_NUM;
    const nextCheck = checked ? 'Y' : 'N';
    setTodos((prev) => prev.map((item) => ((item.job_num || item.JOB_NUM) === num ? { ...item, complete_check: nextCheck } : item)));
    try {
      await postJson('/request/UpdateToDoList', { num, Check: nextCheck });
    } catch (error) {
      setMessage('투두 변경은 서버 로그인 후 반영됩니다.');
    }
  };

  const deleteTodo = async (todo) => {
    const num = todo.job_num || todo.JOB_NUM;
    setTodos((prev) => prev.filter((item) => (item.job_num || item.JOB_NUM) !== num));
    try {
      await postJson('/request/DeleteToDoList', { num });
    } catch (error) {
      setMessage('투두 삭제는 서버 로그인 후 반영됩니다.');
    }
  };

  const moveMonth = (diff) => {
    setVisibleMonth(new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + diff, 1));
  };

  const getBarStyle = (task) => {
    const start = new Date(task.startDate || days[0]);
    const end = new Date(task.endDate || task.startDate || days[0]);
    const base = days[0].getTime();
    const left = Math.max(0, Math.round((start.getTime() - base) / DAY_MS));
    const width = Math.max(1, Math.round((end.getTime() - start.getTime()) / DAY_MS) + 1);
    return {
      left: `${(left / days.length) * 100}%`,
      width: `${Math.min(100 - (left / days.length) * 100, (width / days.length) * 100)}%`,
    };
  };

  return (
    <Shell>
      <SegmentBar>
        <Button type="button" $primary={view === 'timeline'} onClick={() => setView('timeline')}>타임라인</Button>
        <Button type="button" $primary={view === 'todo'} onClick={() => setView('todo')}>투두 캘린더</Button>
        <Badge $variant={isLoggedIn ? 'success' : 'warning'}>{isLoggedIn ? '로그인됨' : '읽기/임시 편집'}</Badge>
        {message && <Badge $variant="default">{message}</Badge>}
      </SegmentBar>

      {view === 'timeline' ? (
        <Dashboard>
          <Panel>
            <PanelHead>
              <h3>프로젝트 타임라인</h3>
              <Badge $variant="primary">30일 보기</Badge>
            </PanelHead>
            <PanelBody>
              <TimelineWrap>
                <Timeline>
                  <TimelineHeader $days={days.length}>
                    {days.map((day) => (
                      <DayCell key={toISO(day)} $today={toISO(day) === toISO(new Date())}>
                        {day.getMonth() + 1}/{day.getDate()}
                      </DayCell>
                    ))}
                  </TimelineHeader>
                  {tasks.map((task) => (
                    <TimelineLine key={task.id}>
                      <Bar style={getBarStyle(task)}>{task.title}</Bar>
                    </TimelineLine>
                  ))}
                </Timeline>
              </TimelineWrap>
            </PanelBody>
          </Panel>

          <Panel>
            <PanelHead>
              <h3>태스크 편집</h3>
              <Badge $variant="default">{tasks.length}개</Badge>
            </PanelHead>
            <PanelBody>
              <TaskList>
                {tasks.map((task) => (
                  <TaskRow key={task.id} type="button" $active={String(task.id) === String(selectedTaskId)} onClick={() => setSelectedTaskId(task.id)}>
                    <div>
                      <strong>{task.title || '-'}</strong>
                      <span>{task.worker || '담당자 미지정'} · {task.startDate || '-'} ~ {task.endDate || '-'}</span>
                    </div>
                    <Badge $variant="default">#{task.id}</Badge>
                  </TaskRow>
                ))}
              </TaskList>
              {taskForm && (
                <form onSubmit={saveTask} style={{ marginTop: 18 }}>
                  <EditorGrid>
                    <Field>작업명<Input value={taskForm.title || ''} onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })} /></Field>
                    <Field>담당자<Input value={taskForm.worker || ''} onChange={(e) => setTaskForm({ ...taskForm, worker: e.target.value })} /></Field>
                    <Field>시작일<Input type="date" value={taskForm.startDate || ''} onChange={(e) => setTaskForm({ ...taskForm, startDate: e.target.value })} /></Field>
                    <Field>종료일<Input type="date" value={taskForm.endDate || ''} onChange={(e) => setTaskForm({ ...taskForm, endDate: e.target.value })} /></Field>
                  </EditorGrid>
                  <Button type="submit" $primary style={{ marginTop: 12 }}>태스크 저장</Button>
                </form>
              )}
            </PanelBody>
          </Panel>
        </Dashboard>
      ) : (
        <TodoLayout>
          <Panel>
            <PanelHead>
              <h3>{visibleMonth.getFullYear()}년 {visibleMonth.getMonth() + 1}월</h3>
              <SegmentBar>
                <Button type="button" onClick={() => moveMonth(-1)}>이전</Button>
                <Button type="button" onClick={() => setVisibleMonth(new Date())}>오늘</Button>
                <Button type="button" onClick={() => moveMonth(1)}>다음</Button>
              </SegmentBar>
            </PanelHead>
            <PanelBody>
              <CalendarGrid>
                {['일', '월', '화', '수', '목', '금', '토'].map((day) => <Badge key={day} $variant="default">{day}</Badge>)}
                {cells.map((cell) => {
                  const iso = toISO(cell);
                  return (
                    <DayButton
                      key={iso}
                      type="button"
                      $active={iso === selectedDate}
                      $muted={cell.getMonth() !== visibleMonth.getMonth()}
                      onClick={() => setSelectedDate(iso)}
                    >
                      {cell.getDate()}
                    </DayButton>
                  );
                })}
              </CalendarGrid>
            </PanelBody>
          </Panel>

          <Panel>
            <PanelHead>
              <h3>{selectedDate} 할 일</h3>
              <Badge $variant="primary">{todos.length}개</Badge>
            </PanelHead>
            <PanelBody>
              <AddRow onSubmit={addTodo}>
                <Input value={todoText} onChange={(e) => setTodoText(e.target.value)} placeholder="할 일을 입력하세요" />
                <Button type="submit" $primary>추가</Button>
              </AddRow>
              {todos.length === 0 ? (
                <Empty>등록된 할 일이 없습니다.</Empty>
              ) : (
                <TaskList>
                  {todos.map((todo) => {
                    const num = todo.job_num || todo.JOB_NUM;
                    const done = (todo.complete_check || todo.COMPLETE_CHECK) === 'Y';
                    return (
                      <TodoItem key={num} $done={done}>
                        <input type="checkbox" checked={done} onChange={(e) => updateTodo(todo, e.target.checked)} />
                        <span>{todo.contents || todo.CONTENTS}</span>
                        <Button type="button" onClick={() => deleteTodo(todo)}>삭제</Button>
                      </TodoItem>
                    );
                  })}
                </TaskList>
              )}
            </PanelBody>
          </Panel>
        </TodoLayout>
      )}
    </Shell>
  );
}

export default WorkspaceCalendarManager;
